/**
 * MONSTER TOWN — POSES AND ACTIONS. MONSTER CREW (src/monster/**).
 *
 * PURE DATA plus the three functions that blend it. `anim.ts` owns the clock and
 * the rig; this file owns WHAT a monster can do. Adding "the monster shivers" is
 * one entry in `ACTIONS` and nothing else in the game changes — the same
 * data-not-engine rule the placeable registry runs on.
 *
 * An unknown action id still animates (it falls back to a nod). A prop that asks
 * for something silly must never leave the monster standing dead still, because
 * LAW #1 says every tap does something a kid can SEE.
 */

// ---------------------------------------------------------------------------
// pose
// ---------------------------------------------------------------------------

export interface Pose {
  bounceY: number
  /** 0..1 fraction of `rig.hipDrop` — how far the hips sink for sit/sleep. */
  crouch: number
  bounceRotX: number
  bounceRotY: number
  bounceRotZ: number
  bodyScaleY: number
  bodyRotX: number
  bodyRotZ: number
  headRotX: number
  headRotY: number
  headRotZ: number
  armLX: number
  armLZ: number
  armRX: number
  armRZ: number
  legLX: number
  legRX: number
  /** 1 = eyes open, 0 = shut. */
  lid: number
  mouth: number
}

export function blankPose(): Pose {
  return {
    bounceY: 0,
    crouch: 0,
    bounceRotX: 0,
    bounceRotY: 0,
    bounceRotZ: 0,
    bodyScaleY: 1,
    bodyRotX: 0,
    bodyRotZ: 0,
    headRotX: 0,
    headRotY: 0,
    headRotZ: 0,
    armLX: 0,
    armLZ: 0.2,
    armRX: 0,
    armRZ: -0.2,
    legLX: 0,
    legRX: 0,
    lid: 1,
    mouth: 1,
  }
}

export function copyPose(from: Pose, to: Pose): void {
  to.bounceY = from.bounceY
  to.crouch = from.crouch
  to.bounceRotX = from.bounceRotX
  to.bounceRotY = from.bounceRotY
  to.bounceRotZ = from.bounceRotZ
  to.bodyScaleY = from.bodyScaleY
  to.bodyRotX = from.bodyRotX
  to.bodyRotZ = from.bodyRotZ
  to.headRotX = from.headRotX
  to.headRotY = from.headRotY
  to.headRotZ = from.headRotZ
  to.armLX = from.armLX
  to.armLZ = from.armLZ
  to.armRX = from.armRX
  to.armRZ = from.armRZ
  to.legLX = from.legLX
  to.legRX = from.legRX
  to.lid = from.lid
  to.mouth = from.mouth
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export function blendPose(base: Pose, over: Pose, k: number, out: Pose): void {
  out.bounceY = lerp(base.bounceY, over.bounceY, k)
  out.crouch = lerp(base.crouch, over.crouch, k)
  out.bounceRotX = lerp(base.bounceRotX, over.bounceRotX, k)
  out.bounceRotY = lerp(base.bounceRotY, over.bounceRotY, k)
  out.bounceRotZ = lerp(base.bounceRotZ, over.bounceRotZ, k)
  out.bodyScaleY = lerp(base.bodyScaleY, over.bodyScaleY, k)
  out.bodyRotX = lerp(base.bodyRotX, over.bodyRotX, k)
  out.bodyRotZ = lerp(base.bodyRotZ, over.bodyRotZ, k)
  out.headRotX = lerp(base.headRotX, over.headRotX, k)
  out.headRotY = lerp(base.headRotY, over.headRotY, k)
  out.headRotZ = lerp(base.headRotZ, over.headRotZ, k)
  out.armLX = lerp(base.armLX, over.armLX, k)
  out.armLZ = lerp(base.armLZ, over.armLZ, k)
  out.armRX = lerp(base.armRX, over.armRX, k)
  out.armRZ = lerp(base.armRZ, over.armRZ, k)
  out.legLX = lerp(base.legLX, over.legLX, k)
  out.legRX = lerp(base.legRX, over.legRX, k)
  out.lid = lerp(base.lid, over.lid, k)
  out.mouth = lerp(base.mouth, over.mouth, k)
}

// ---------------------------------------------------------------------------
// actions — DATA. `u` is 0..1 through the action, `t` is the global clock.
// ---------------------------------------------------------------------------

export interface ActionDef {
  /** Default length in seconds. 0 = hold until something else happens. */
  readonly seconds: number
  /** True if the action survives the monster starting to walk (carry). */
  readonly whileWalking?: boolean
  readonly pose: (p: Pose, u: number, t: number) => void
}

export const TAU = Math.PI * 2

export const ACTIONS: Record<string, ActionDef> = {
  nod: {
    seconds: 0.9,
    pose: (p, u) => {
      p.headRotX = Math.sin(u * TAU * 2) * 0.32
    },
  },
  shake: {
    seconds: 0.9,
    pose: (p, u) => {
      p.headRotY = Math.sin(u * TAU * 2.5) * 0.42
    },
  },
  wave: {
    seconds: 1.6,
    pose: (p, u) => {
      p.armRX = -2.2
      p.armRZ = -0.5 + Math.sin(u * TAU * 3) * 0.55
      p.headRotZ = -0.1
      p.bounceY = Math.abs(Math.sin(u * TAU * 3)) * 0.03
    },
  },
  sniff: {
    seconds: 1.2,
    pose: (p, u) => {
      const dip = Math.sin(Math.min(1, u * 1.4) * Math.PI)
      p.bounceRotX = 0.42 * dip
      p.headRotX = 0.35 * dip + Math.sin(u * TAU * 6) * 0.05
      p.armLX = -0.5 * dip
      p.armRX = -0.5 * dip
      p.bounceY = -0.05 * dip
      p.lid = 1 - 0.5 * dip
    },
  },
  sit: {
    seconds: 0,
    pose: (p, _u, t) => {
      p.crouch = 0.95
      p.legLX = -1.35
      p.legRX = -1.35
      p.bodyRotX = -0.1
      p.armLZ = 0.42
      p.armRZ = -0.42
      p.headRotY = Math.sin(t * 0.6) * 0.2
      p.bodyScaleY = 0.96 + Math.sin(t * 2) * 0.02
    },
  },
  /**
   * SITTING IN A CHAIR, as opposed to `sit`'s cross-legged-on-the-floor.
   *
   * `sit` throws the legs almost flat forward (-1.35), which is right on a rug
   * and wrong on a seat: at hip height, a horizontal leg lies INSIDE the cushion
   * it is meant to be dangling over, and every seated customer in the venue lost
   * its lower half. Here they hang forward and DOWN over the front lip, the body
   * leans back into the chair, and the arms come out to rest on its arms.
   */
  sitChair: {
    seconds: 0,
    pose: (p, _u, t) => {
      p.crouch = 0.95
      p.legLX = -0.95
      p.legRX = -0.95
      p.bodyRotX = -0.07
      p.armLX = 0.22
      p.armRX = 0.22
      p.armLZ = 0.52
      p.armRZ = -0.52
      p.headRotY = Math.sin(t * 0.6) * 0.18
      p.bodyScaleY = 0.95 + Math.sin(t * 2) * 0.02
    },
  },
  sleep: {
    seconds: 0,
    pose: (p, _u, t) => {
      p.crouch = 1
      p.legLX = -1.5
      p.legRX = -1.5
      p.bodyRotX = 0.3
      p.headRotX = 0.46
      p.headRotZ = 0.24
      p.armLZ = 0.62
      p.armRZ = -0.62
      p.lid = 0
      // Slow, obvious breathing so a sleeping monster is clearly asleep and
      // not just a prop a kid has stopped being able to interact with.
      p.bodyScaleY = 1 + Math.sin(t * 1.4) * 0.06
      p.bounceRotZ = 0.1
    },
  },
  eat: {
    seconds: 1.8,
    pose: (p, u) => {
      const chew = Math.sin(u * TAU * 5)
      p.armLX = -1.9
      p.armRX = -1.9
      p.armLZ = 0.55
      p.armRZ = -0.55
      p.headRotX = 0.12 + chew * 0.12
      p.mouth = 1 + chew * 0.6
      p.bodyScaleY = 1 + chew * 0.03
      p.lid = 0.7
    },
  },
  cheer: {
    seconds: 1.8,
    pose: (p, u) => {
      const hop = Math.abs(Math.sin(u * TAU * 3))
      p.bounceY = hop * 0.26
      p.armLX = -2.5
      p.armRX = -2.5
      p.armLZ = 0.5
      p.armRZ = -0.5
      p.headRotX = -0.22
      p.bodyScaleY = 1 + (1 - hop) * -0.08 + hop * 0.06
      p.mouth = 1.8
    },
  },
  clap: {
    seconds: 1.4,
    pose: (p, u) => {
      const c = Math.sin(u * TAU * 5)
      p.armLX = -1.5
      p.armRX = -1.5
      p.armLZ = 0.9 + c * 0.35
      p.armRZ = -0.9 - c * 0.35
      p.headRotX = -0.1
      p.bounceY = Math.abs(c) * 0.03
    },
  },
  /**
   * PLOPPED DOWN IN FRONT OF THE TELLY.
   *
   * A held pose (`seconds: 0`) on purpose. `dance` is 1.8 seconds of small
   * limb motion, and at interior zoom a monster is about a hundred pixels
   * tall — two frames twenty apart came back looking identical, and the whole
   * tap read as dead. A kid needs to be able to look away, look back, and
   * still see that the telly is on and their monster is watching it.
   *
   * So this one changes the SILHOUETTE and then stays: hips down on the floor,
   * legs out front, head tipped up at the screen, with a slow happy sway and
   * the occasional bounce. Unmistakable at any zoom, and it holds until the
   * kid taps something else or walks away (LAW #2 — nothing expires).
   */
  watchTv: {
    seconds: 0,
    pose: (p, _u, t) => {
      const sway = Math.sin(t * 2.2)
      const bop = Math.abs(Math.sin(t * 4.4))
      p.crouch = 0.95
      p.legLX = -1.45
      p.legRX = -1.45
      p.bodyRotX = -0.16
      p.bodyRotZ = sway * 0.07
      p.bounceY = bop * 0.045
      p.bounceRotZ = sway * 0.06
      // Head up at the screen, nodding along.
      p.headRotX = -0.3 + bop * 0.08
      p.headRotZ = sway * 0.13
      p.armLX = -0.35
      p.armRX = -0.35
      p.armLZ = 0.62 + sway * 0.16
      p.armRZ = -0.62 - sway * 0.16
      p.bodyScaleY = 0.97 + bop * 0.035
      p.mouth = 1.5
    },
  },
  dance: {
    seconds: 0,
    pose: (p, _u, t) => {
      const s = Math.sin(t * 5)
      const s2 = Math.sin(t * 10)
      p.bounceY = Math.abs(s2) * 0.11
      p.bounceRotZ = s * 0.16
      p.bounceRotY = s * 0.3
      p.armLX = -1.6 - s * 0.8
      p.armRX = -1.6 + s * 0.8
      p.armLZ = 0.4
      p.armRZ = -0.4
      p.headRotZ = -s * 0.16
      p.bodyScaleY = 1 + s2 * 0.05
    },
  },
  spin: {
    seconds: 1.1,
    pose: (p, u) => {
      p.bounceRotY = u * TAU
      p.bounceY = Math.sin(u * Math.PI) * 0.12
      p.armLZ = 1.1
      p.armRZ = -1.1
    },
  },
  hop: {
    seconds: 0.7,
    pose: (p, u) => {
      const j = Math.sin(u * Math.PI)
      p.bounceY = j * 0.3
      p.bodyScaleY = 1 + j * 0.1 - (1 - j) * 0.06
      p.legLX = -0.5 * j
      p.legRX = -0.5 * j
      p.armLX = -1.2 * j
      p.armRX = -1.2 * j
    },
  },
  shrug: {
    seconds: 1.2,
    pose: (p, u) => {
      const s = Math.sin(Math.min(1, u * 1.3) * Math.PI)
      p.armLZ = 0.12 + s * 1.1
      p.armRZ = -0.12 - s * 1.1
      p.armLX = -0.4 * s
      p.armRX = -0.4 * s
      p.headRotZ = s * 0.14
      p.bounceY = s * 0.03
    },
  },
  laugh: {
    seconds: 1.5,
    pose: (p, u) => {
      const s = Math.sin(u * TAU * 6)
      p.headRotX = -0.28 + s * 0.1
      p.bodyScaleY = 1 + s * 0.07
      p.bounceY = Math.abs(s) * 0.05
      p.armLZ = 0.5
      p.armRZ = -0.5
      p.mouth = 2.2
      p.lid = 0.25
    },
  },
  carry: {
    seconds: 0,
    whileWalking: true,
    pose: (p) => {
      p.armLX = -1.5
      p.armRX = -1.5
      p.armLZ = 0.25
      p.armRZ = -0.25
    },
  },
  knock: {
    seconds: 0.7,
    pose: (p, u) => {
      const k = Math.sin(u * TAU * 3)
      p.armRX = -1.6 - Math.abs(k) * 0.5
      p.armRZ = -0.2
      p.bodyRotX = 0.08
      p.headRotX = 0.1
    },
  },
  point: {
    seconds: 1.1,
    pose: (p) => {
      p.armRX = -1.7
      p.armRZ = -0.15
      p.headRotX = 0.06
    },
  },
  wiggle: {
    seconds: 1.1,
    pose: (p, u) => {
      const s = Math.sin(u * TAU * 4)
      p.bounceRotZ = s * 0.12
      p.headRotZ = -s * 0.16
      p.bodyScaleY = 1 + Math.sin(u * TAU * 8) * 0.03
    },
  },
  lookAround: {
    seconds: 2.2,
    pose: (p, u) => {
      p.headRotY = Math.sin(u * TAU) * 0.7
      p.headRotX = -0.05
    },
  },
  think: {
    seconds: 1.6,
    pose: (p, u) => {
      p.armRX = -2.1
      p.armRZ = -0.65
      p.headRotZ = 0.18
      p.headRotY = Math.sin(u * TAU * 0.5) * 0.2
      p.lid = 0.6
    },
  },
  sad: {
    seconds: 1.6,
    pose: (p) => {
      p.headRotX = 0.42
      p.bodyRotX = 0.14
      p.armLZ = 0.02
      p.armRZ = -0.02
      p.lid = 0.5
      p.crouch = 0.2
    },
  },
}

/** Aliases so any crew's vocabulary lands on something visible. */
const ALIASES: Record<string, string> = {
  celebrate: 'cheer',
  happy: 'cheer',
  jump: 'hop',
  bounce: 'hop',
  smell: 'sniff',
  taste: 'eat',
  sip: 'eat',
  drink: 'eat',
  scoop: 'carry',
  serve: 'carry',
  hold: 'carry',
  nap: 'sleep',
  rest: 'sit',
  wash: 'clap',
  play: 'wiggle',
  wonder: 'think',
  greet: 'wave',
  hello: 'wave',
  bye: 'wave',
  yes: 'nod',
  no: 'shake',
  confused: 'shake',
  giggle: 'laugh',
  boogie: 'dance',
  twirl: 'spin',
  watch: 'watchTv',

  // Found by the cross-crew wiring gate: content asked the monster to do these
  // six things and none of them existed, so tapping the stove ("cook"), the
  // bookshelf ("read"), the toy box ("tidy"), a stair ("climb"), a block
  // ("stomp") or the pond ("splash") all silently degraded to the same generic
  // nod. LAW #1 was satisfied on paper and dull in the hand. These are the
  // closest honest poses; each still deserves a pose of its own one day.
  cook: 'clap',
  read: 'think',
  tidy: 'carry',
  climb: 'hop',
  stomp: 'hop',
  splash: 'wiggle',
}

export function resolveAction(action: string): string {
  if (ACTIONS[action] !== undefined) return action
  const alias = ALIASES[action]
  if (alias !== undefined && ACTIONS[alias] !== undefined) return alias
  return 'nod'
}

/** Every action id a crew may send, for docs and the harness. */
export function actionIds(): string[] {
  return Object.keys(ACTIONS)
}
